<template>
  <!-- Login page: full-screen, no shell -->
  <router-view v-if="isAuthPage" />

  <!-- All other pages: shell with sidebar -->
  <v-sheet v-else class="fill-height mb-5">
    <v-layout>

      <!-- Top App Bar -->
      <v-app-bar :color="brandColor" elevation="2" density="comfortable">
        <v-app-bar-nav-icon variant="text" @click.stop="rail = !rail" />
        <v-toolbar-title class="d-flex align-center" style="gap: 10px;">
          <v-avatar size="32">
            <v-img src="/uploads/logo.jpg" cover />
          </v-avatar>
          <span class="text-subtitle-1 font-weight-bold text-white">Sunrise Events</span>
        </v-toolbar-title>
        <v-spacer />
        <v-tooltip text="Scan Attendee" location="bottom">
          <template v-slot:activator="{ props }">
            <router-link :to="{ name: 'scan-attendee' }">
              <v-btn icon="mdi-qrcode-scan" color="white" variant="text" v-bind="props" />
            </router-link>
          </template>
        </v-tooltip>
      </v-app-bar>

      <!-- Navigation Drawer -->
      <v-navigation-drawer v-model="drawer" :rail="rail" permanent :color="brandColor">

        <!-- User info -->
        <v-list>
          <v-list-item
            prepend-avatar="/uploads/logo.jpg"
            :title="currentUser?.username || currentUser?.name || 'Administrator'"
          >
            <template v-slot:subtitle>
              <v-chip size="x-small" color="white" variant="outlined" class="mt-1">
                {{ currentUser?.role || 'Admin' }}
              </v-chip>
            </template>
          </v-list-item>
        </v-list>

        <v-divider />

        <!-- Navigation items -->
        <v-list density="compact" nav class="mt-1">
          <v-list-item
            prepend-icon="mdi-view-dashboard"
            title="Dashboard"
            :to="{ name: 'dashboard' }"
            link
            rounded="lg"
            active-color="white"
          />

          <v-list-item
            v-if="isAdmin"
            prepend-icon="mdi-calendar-multiple"
            title="Events"
            to="/"
            link
            rounded="lg"
            active-color="white"
          />
          <v-list-item
            prepend-icon="mdi-email-send-outline"
            title="Invitations"
            to="/invitation"
            link
            rounded="lg"
            active-color="white"
          />

          <v-list-item
            prepend-icon="mdi-qrcode-scan"
            title="Scan Attendee"
            to="/scan-attendee"
            link
            rounded="lg"
            active-color="white"
          />
          <v-list-item
            v-if="isAdmin"
            prepend-icon="mdi-account-cog"
            title="Users"
            to="/users"
            link
            rounded="lg"
            active-color="white"
          />
          <v-list-item
            v-if="isAdmin"
            prepend-icon="mdi-email-edit-outline"
            title="Email Template"
            to="/email-template"
            link
            rounded="lg"
            active-color="white"
          />

          <v-divider class="my-2" />

          <v-list-item
            prepend-icon="mdi-help-circle-outline"
            title="Help"
            to="/help"
            link
            rounded="lg"
            active-color="white"
          />
          <v-list-item
            prepend-icon="mdi-information-outline"
            title="About"
            to="/about"
            link
            rounded="lg"
            active-color="white"
          />

          <v-divider class="my-2" />

          <v-list-item
            prepend-icon="mdi-logout"
            title="Logout"
            @click="logout"
            rounded="lg"
            base-color="red-lighten-3"
          />
        </v-list>
      </v-navigation-drawer>

      <v-main>
        <router-view />
      </v-main>
    </v-layout>

    <v-footer :color="brandColor" style="position: fixed; bottom: 0; width: 100%;" height="36">
      <div class="text-center ma-auto text-caption">
        <v-icon size="14" class="mr-1">mdi-copyright</v-icon>
        {{ new Date().getFullYear() }} &mdash; <strong>SUNRISE EVENTS</strong>
      </div>
    </v-footer>
  </v-sheet>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";

const router = useRouter();
const route  = useRoute();
const drawer = ref(true);
const rail   = ref(false);

const isAuthPage = computed(() => route.name === "login");

const brandColor = ref(localStorage.getItem("brandColor") || "#212121");

function onBrandColorChanged(e) {
  brandColor.value = e.detail;
}

onMounted(() => {
  window.addEventListener("brandColorChanged", onBrandColorChanged);
});

onUnmounted(() => {
  window.removeEventListener("brandColorChanged", onBrandColorChanged);
});

const currentUser = computed(() => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
});

const isAdmin = computed(() => currentUser.value?.role === "Admin");

function logout() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  router.push("/login");
}
</script>
